/*! JointJS+ v4.0.1 - HTML5 Diagramming Framework - TRIAL VERSION

Copyright (c) 2024 client IO

 2024-10-28


This Source Code Form is subject to the terms of the JointJS+ Trial License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/


import {dia, ui} from '@joint/plus';
import * as appShapes from '../../shapes/app-shapes';

export class StencilService {

  stencil: ui.Stencil;

  create(paperScroller: ui.PaperScroller, snaplines: ui.Snaplines) {

    this.stencil = new ui.Stencil({
      paper: paperScroller,
      snaplines: snaplines,
      scaleClones: true,
      label: 'STENCIL',
      groups: this.getStencilGroups(),
      dropAnimation: true,
      groupsToggleButtons: true,
      paperOptions: function () {
        return {
          model: new dia.Graph({}, {
            cellNamespace: appShapes
          }),
          cellViewNamespace: appShapes
        };
      },
      search: {
        '*': ['type', 'attrs/root/dataTooltip', 'attrs/label/text'],
      },
      layout: {
        columns: 1,
        marginX: 200,
        horizontalAlign: 'middle',
        columnGap: 10,
        columnWidth: 1,
        rowHeight: 80,
      },
      // Remove tooltip definition from clone
      dragStartClone: (cell: dia.Cell) =>  {
        // On enlève le text et le tooltip avant de drop l'élément.
        const cellCloneWithoutTooltip = cell.clone().removeAttr('root/dataTooltip');

        // si c'est l'élément fin, on garde le texte, on enlève juste le tooltip
        if(cell.attributes?.['isFinProcedure'] ) {
          return cellCloneWithoutTooltip
        } else {
          return cellCloneWithoutTooltip.attr('label/text', '');
        }
      }
    });
  }

  setShapes() {
    this.stencil.load(this.getStencilShapes());

    // On met a jour le placeholder de l'input qui permet la recherche des "shapes"
    const inputElement: any = this.stencil.el.querySelector('.search');
    if (inputElement) {
      inputElement.placeholder = 'Rechercher un élément';
    }
  }

  getStencilGroups() {
    return <{ [key: string]: ui.Stencil.Group }>{
      standard: {index: 1, label: 'Formes standards'},
      image: {index: 2, label: 'Images', closed: true},
    };
  }

  getStencilShapes() {
    return {
      standard: [
        {
          type: 'app.RectangularModel',
          size: {
            width: 360, height: 50
          },
          minHeight: 50,
          // On ajoute cet attribut pour diff le titre des autres
          isTitreProcedure: true,
          // On ajoute cet attribut pour savoir s'il est bold par default
          hasDefaultBoldText: true,
          scale: 2,
          allowOrthogonalResize: false,
          defaultText: 'Titre',
          attrs: {
            root: {
              dataTooltip: 'Bloc Titre',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil',
            },
            body: {
              fill: '#b5afe1',
              stroke: '#000000',
              strokeWidth: 2,
              strokeDasharray: '0',
              rx: 25,
              ry: 25
            },
            label: {
              text: 'Titre procédure',
              textDecoration: 'none',
              textWrap: {
                width: 'calc(w - 30)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: '#000000',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'bold',
              fontSize: 18,
              strokeWidth: 0
            }
          },
          ports: {
            groups: {
              out: {
                position: { name: 'bottom' },
                attrs: {
                  portBody: {
                    magnet: true,
                    r: 3,
                    fill: '#6a6c8a',
                    stroke: '#023047'
                  },
                  text: {fill: '#6a6c8a'}
                }
              }
            },
            items: [
              {group: 'out'}]
          },
        },
        {
          type: 'standard.Path',
          size: { width: 300, height: 50 },
          minHeight: 50,
          allowOrthogonalResize: false,
          defaultText: 'Information',
          attrs: {
            root: {
              dataTooltip: 'Bloc Information',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              d: `
                M calc(0.1*w) 0
                L calc(w) 0
                L calc(0.9*w) calc(h)
                L 0 calc(h)
                Z
              `,
              fill: 'white',
              stroke: '#000000',
              strokeWidth: 1,
            },
            label: {
              text: 'Information',
              textWrap: {
                width: 'calc(w - 60)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: 'black',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 14,
              textDecoration: 'none',
              strokeWidth: 0
            }
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},
                attrs: {
                  circle: {r: 3, magnet: true, strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              out: {
                position: {name: 'bottom'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              }
            },
            items: [
              {group: 'in'}, {group: 'out'}]
          }
        },
        {
          type: 'standard.Path',
          size: {width: 300, height: 50},
          minHeight: 50,
          allowOrthogonalResize: false,
          isBlocQuestion: true,
          defaultText: 'Question',
          attrs: {
            root: {
              dataTooltip: 'Bloc Question',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              d:
                ` M 0 calc(0.5*h)
                  L calc(0.05*w) 0 calc(0.95*w) 0 calc(w) calc(0.5*h) calc(0.95*w) calc(h) calc(0.05*w) calc(h)
                  Z `,
              fill: '#FFF176',
              stroke: '#000000',
              strokeWidth: 1,
              strokeDasharray: '0'
            },
            label: {
              text: 'Question',
              textWrap: {
                width: 'calc(w - 30)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: 'black',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 14,
              textDecoration: 'none',
              strokeWidth: 0
            }
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              outLeft: {
                position: {name: 'left'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    text: 'Non',
                    ref: 'circle',
                    refX: -20,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              },
              outRight: {
                position: {name: 'right'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    text: 'Oui',
                    ref: 'circle',
                    refX: 40,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              }
            },
            items: [
              {group: 'in'},
              {group: 'outLeft'},
              {group: 'outRight'}
            ]
          }
        },
        {
          type: 'standard.Rectangle',
          size: {width: 300, height: 50},
          minHeight: 50,
          defaultText: 'Action',
          attrs: {
            root: {
              dataTooltip: 'Bloc Action',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              fill: '#F5F5F5',
              stroke: '#000000',
              strokeWidth: 1,
            },
            label: {
              text: 'Action',
              textWrap: {
                width: 'calc(w - 30)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: '#000000',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 14,
              textDecoration: 'none',
              strokeWidth: 2
            },
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},
                attrs: {
                  circle: {r: 3, magnet: true, strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              out: {
                position: {name: 'bottom'},
                attrs: {
                  circle: {r: 3, magnet: true, strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              outLeft: {
                position: {name: 'left'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    ref: 'circle',
                    refX: -20,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              },
              outRight: {
                position: {name: 'right'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    ref: 'circle',
                    refX: 40,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              }
            },
            items: [
              {group: 'in'},
              {group: 'out'},
              {group: 'outLeft'},
              {group: 'outRight'}
            ]
          }
        },
        {
          type: 'standard.Rectangle',
          size: {width: 300, height: 50},
          minHeight: 50,
          // On ajoute cet attribut pour savoir s'il est bold par default
          hasDefaultBoldText: true,
          defaultText: 'Action sécuritaire',
          attrs: {
            root: {
              dataTooltip: 'Bloc action sécuritaire',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              rx: 2,
              ry: 2,
              fill: '#FFFFFF',
              stroke: 'red',
              strokeWidth: 2,
            },
            label: {
              text: 'Action sécuritaire',
              textWrap: {
                width: 'calc(w - 30)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: 'red',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Bold',
              fontSize: 14,
              textDecoration: 'none',
              strokeWidth: 0
            },
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              out: {
                position: {name: 'bottom'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              outLeft: {
                position: {name: 'left'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    ref: 'circle',
                    refX: -20,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              },
              outRight: {
                position: {name: 'right'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    ref: 'circle',
                    refX: 40,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              }
            },
            items: [
              {group: 'in'},
              {group: 'out'},
              {group: 'outLeft'},
              {group: 'outRight'}
            ]
          }
        },
        {
          type: 'standard.EmbeddedImage',
          size: {width: 300, height: 50},
          minHeight: 50,
          // On ajoute cet attribut pour savoir s'il est bold par default
          hasDefaultBoldText: true,
          defaultText: 'Action sécuritaire avec action coup de poing',
          attrs: {
            root: {
              dataTooltip: 'Bloc Action sécuritaire avec action coup de poing',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              fill: '#FFFFFF',
              stroke: 'red',
              strokeWidth: 2,
            },
            image: {
              xlinkHref: 'assets/logo-coup-poing.png',
                width: 60,
                height: 40,
            },
            label: {
              text: 'Action sécuritaire avec action coup de poing',
              fill: 'red',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Bold',
              textWrap: {
                width: 'calc(w - 100)',
                height: null,
                preserveSpaces: true
                //marginLeft: 80
              },
              displayEmpty: true,
              textAnchor: 'middle',
              //textVerticalAnchor: 'middle',
              fontSize: 14,
              textDecoration: 'none',
              strokeWidth: 0,
              // pour laisser l'image a gauche et pouvoir centrer le text sur le reste de l'élément
              transform: 'translate(80, 0)'
            },
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              out: {
                position: {name: 'bottom'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              outLeft: {
                position: {name: 'left'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    ref: 'circle',
                    refX: -20,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              },
              outRight: {
                position: {name: 'right'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    ref: 'circle',
                    refX: 40,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              }
            },
            items: [
              {group: 'in'},
              {group: 'out'},
              {group: 'outLeft'},
              {group: 'outRight'}
            ]
          }
        },
        // Bloc diffusion (pour le moment, on commente).
       /*
          {
          type: 'standard.Rectangle',
          size: {width: 300, height: 50},
          attrs: {
            root: {
              dataTooltip: 'Rectangle',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              fill: '#99ccff',
              stroke: 'black',
              strokeWidth: 1,
              strokeDasharray: '0'
            },
            label: {
              text: 'Diffuser le message de la rame',
              textWrap: {
                width: 'calc(w - 30)',
              },
              fill: '#000000',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 14,
              textDecoration: 'none',
              strokeWidth: 0
            },
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},
                attrs: {
                  circle: {r: 3, magnet: true, strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              out: {
                position: {name: 'bottom'},
                attrs: {
                  circle: {r: 3, magnet: true, strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              outLeft: {
                position: {name: 'left'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    ref: 'circle',
                    refX: -20,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              },
              outRight: {
                position: {name: 'right'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {
                    fill: '#6a6c8a',
                    ref: 'circle',
                    refX: 40,
                    refY: -20,
                    yAlignment: 'middle'
                  }
                }
              }
            },
            items: [
              {group: 'in'},
              {group: 'out'},
              {group: 'outLeft'},
              {group: 'outRight'}
            ]
          }
        },
        */
        {
          type: 'app.RectangularRenvoiModel',
          size: {width: 100, height: 50},
          minHeight: 50,
          allowOrthogonalResize: false,
          defaultText: 'Renvoi',
          attrs: {
            root: {
              dataTooltip: 'Bloc Renvoi',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil',
            },
            body: {
              fill: '#b5afe1',
              stroke: '#000000',
              strokeWidth: 1,
              strokeDasharray: '0',
              rx: 25,
              ry: 25
            },
            label: {
              text: 'Renvoi',
              textWrap: {
                width: 'calc(w - 30)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: '#000000',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 12,
              textDecoration: 'none',
              strokeWidth: 0
            }
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},

                attrs: {
                  portBody: {
                    magnet: true,
                    r: 3,
                    fill: '#6a6c8a',
                    stroke: '#023047'
                  },
                  text: {fill: '#6a6c8a'}
                }
              },
              out: {
                position: {name: 'bottom'},

                attrs: {
                  portBody: {
                    magnet: true,
                    r: 3,
                    fill: '#6a6c8a',
                    stroke: '#023047'
                  },
                  text: {fill: '#6a6c8a'}
                }
              },
            },
            items: [
              {group: 'in'},
              {group: 'out'}
            ]
          }
        },
        {
          type: 'app.CircularModel',
          size: {width: 50, height: 50},
          minHeight: 50,
          allowOrthogonalResize: false,
          defaultText: 'Point d\'information',
          attrs: {
            root: {
              dataTooltip: "Bloc Point d'information",
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              fill: '#F5F5F5',
              stroke: '#000000',
              strokeWidth: 1,
              strokeDasharray: '0',
            },
            label: {
              text: 'PX',
              textWrap: {
                width: 'calc(w - 30)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: '#000000',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 16,
              textDecoration: 'none',
              titleOfDialog: "Point d'information",
              strokeWidth: 0
            }
          },
        },
        /*
        On commente pour le moment le bloc SX et MX
        {
          type: 'app.CircularModel',
          size: {width: 50, height: 50},
          allowOrthogonalResize: false,
          attrs: {
            root: {
              dataTooltip: 'Circle without ports',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              fill: 'red',
              stroke: 'transparent',
              strokeWidth: 1,
              strokeDasharray: '0',
            },
            label: {
              text: 'SX',
              textWrap: {
                width: 'calc(w - 30)',
              },
              fill: '#000000',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 16,
              textDecoration: 'none',
              strokeWidth: 0,
              titleOfDialog: "Point sécuritaire",
            }
          },
        },
        */
        {
          type: 'standard.Path',
          size: {width: 50, height: 50},
          minHeight: 50,
          allowOrthogonalResize: false,
          condition: 'renvoi',
          defaultText: 'Renvoi vers page',
          attrs: {
            root: {
              dataTooltip: 'Bloc Renvoi vers page',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              d: `
                  M 0 0
                  L calc(w) 0
                  L calc(w) calc(h - 25)
                  L calc(0.5*w) calc(h)
                  L 0 calc(h - 25)
                  Z
              `,
              fill: 'white',
              stroke: 'black',
              strokeWidth: 1,
              strokeDasharray: '0',
            },
            label: {
              text: 'X',
              textWrap: {
                width: 'calc(w - 30)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: 'black',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 18,
              textDecoration: 'none',
              strokeWidth: 0,
            }
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},
                attrs: {
                  circle: {r: 3, magnet: true, strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              },
              out: {
                position: {name: 'bottom'},
                attrs: {
                  circle: {r: 3, magnet: true, stroke: '#023047', strokeWidth: 0, fill: '#6a6c8a'},
                  text: {fill: '#6a6c8a'}
                }
              }
            },
            items: [
              {group: 'in'}, {group: 'out'}]
          }
        },
        /*
        On Commente pour le moment le bloc MX
        {
          type: 'app.CircularModel',
          size: {width: 50, height: 50},
          allowOrthogonalResize: false,
          attrs: {
            root: {
              dataTooltip: 'Circle without ports',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              fill: '#006fff',
              stroke: 'transparent',
              strokeWidth: 1,
              strokeDasharray: '0',
            },
            label: {
              text: 'MX',
              textWrap: {
                width: 'calc(w - 30)',
              },
              fill: 'black',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 18,
              textDecoration: 'none',
              strokeWidth: 0,
              titleOfDialog: "Point message",
            }
          },
        },
        */
        {
          type: 'app.RectangularModel',
          size: {width: 80, height: 50},
          minHeight: 50,
          allowOrthogonalResize: false,
          isFinProcedure: true,
          // On ajoute cet attribut pour savoir s'il est bold par default
          hasDefaultBoldText: true,
          defaultText: 'Fin',
          attrs: {
            root: {
              dataTooltip: 'Bloc Fin',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil',
            },
            body: {
              fill: '#a5a5a5',
              stroke: '#000000',
              strokeWidth: 2,
              strokeDasharray: '0',
              rx: 25,
              ry: 25
            },
            label: {
              text: 'Fin',
              textWrap: {
                width: 'calc(w - 30)',
                height: null,
                preserveSpaces: true
              },
              displayEmpty: true,
              fill: '#000000',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Bold',
              fontSize: 18,
              textDecoration: 'none',
              strokeWidth: 0
            }
          },
          ports: {
            groups: {
              in: {
                position: {name: 'top'},
                attrs: {
                  portBody: {
                    magnet: true,
                    r: 3,
                    fill: '#6a6c8a',
                    stroke: '#023047'
                  },
                  text: {fill: '#6a6c8a'}
                }
              },
            },
            items: [
              {group: 'in'},]
          }
        },
        /*
        {
          type: 'standard.HeaderedRectangle',
          size: {width: 90, height: 54},
          attrs: {
            root: {
              dataTooltip: 'Rectangle with header',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              fill: 'transparent',
              stroke: '#31d0c6',
              strokeWidth: 2,
              strokeDasharray: '0'
            },
            header: {
              stroke: '#31d0c6',
              fill: '#31d0c6',
              strokeWidth: 2,
              strokeDasharray: '0',
              height: 20
            },
            bodyText: {
              textWrap: {
                text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur molestie.',
                width: -10,
                height: -20,
                ellipsis: true
              },
              fill: '#c6c7e2',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 11,
              strokeWidth: 0,
              y: 'calc(h/2 + 10)',
            },
            headerText: {
              text: 'header',
              fill: '#f6f6f6',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 11,
              strokeWidth: 0,
              y: 10
            }
          }
        },
        */
      ],
      image: [
        {
          type: 'standard.Image',
          size: {width: 80, height: 50},
          minHeight: 50,
          defaultText: 'Image',
          attrs: {
            root: {
              dataTooltip: 'Image',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            image: {
              xlinkHref: 'assets/image-icon1.svg'
            },
            body: {
              fill: 'transparent',
              stroke: '#31d0c6',
              strokeWidth: 2,
              strokeDasharray: '0'
            },
            label: {
              text: 'image',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 11,
              fill: '#c6c7e2'
            }
          }
        },
        /*
        {
          type: 'standard.EmbeddedImage',
          size: {width: 90, height: 54},
          attrs: {
            root: {
              dataTooltip: 'Card',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            body: {
              fill: 'transparent',
              stroke: '#31d0c6',
              strokeWidth: 2,
              strokeDasharray: '0'
            },
            image: {
              xlinkHref: 'assets/image-icon1.svg'
            },
            label: {
              text: 'card',
              fill: '#c6c7e2',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 11,
              strokeWidth: 0
            }
          }
        },
        */
        {
          type: 'standard.InscribedImage',
          size: {width: 60, height: 60},
          minHeight: 60,
          defaultText: 'Icon',
          attrs: {
            root: {
              dataTooltip: 'Icon',
              dataTooltipPosition: 'left',
              dataTooltipPositionSelector: '.joint-stencil'
            },
            border: {
              stroke: '#31d0c6',
              strokeWidth: 3,
              strokeDasharray: '0'
            },
            background: {
              fill: 'transparent'
            },
            image: {
              xlinkHref: 'assets/image-icon1.svg'
            },
            label: {
              text: 'icon',
              fill: '#c6c7e2',
              fontFamily: 'Roboto Condensed',
              fontWeight: 'Normal',
              fontSize: 11,
              strokeWidth: 0
            }
          }
        },
      ]
    };
  }
}
